import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalBody } from 'reactstrap';

import CustomCheckboxDropdown from '../../../components/CustomCheckboxDropdown/CustomCheckboxDropdown';
import CreateProjectDropdown from '../../../components/DropdownStyle/CreateProjectDropdown';
import ButtonTextLoader from '../../../components/Loaders/ButtonTextLoader';
import Select from '../../../components/ReactSelectDropdown';
import { filterOption } from '../../../components/ReactSelectDropdown/ReactSelectDropdown';
import { PrimaryButton, WhiteButton } from '../../../styledComponents/buttons';
import {
  CommonGrid,
  CommonText,
  FormAlert,
  InputField,
  InputLabel,
  InputWithLabelSection,
  RedStar,
} from '../../../styledComponents/common';
import { checkHtml, checkUrl } from '../../../utils/helper';

const AddMasterTaskModal = ({
  isOpen,
  toggle,
  publicZoneList,
  getOfficesShortList,
  officesShortList,
  addMasterTask,
  isLoading,
  reload,
  setReload,
  editMasterTask,
  taskToEdit,
  isEditMode,
}) => {
  console.log('🚀 ~ AddMasterTaskModal.jsx:36 ~ taskToEdit:', taskToEdit);
  const { t } = useTranslation();

  const [taskName, setTaskName] = useState('');
  const [maxTaskDuration, setMaxTaskDuration] = useState('');
  const [defaultTaskDuration, setDefaultTaskDuration] = useState('');
  const [saveErrors, setSaveErrors] = useState({});
  const [selectedPublicZone, setSelectedPublicZone] = useState('');
  const [publicZoneOptions, setPublicZoneOptions] = useState([]);
  const [selectedOffices, setSelectedOffices] = useState([]);
  const [officeOptions, setOfficeOptions] = useState([]);
  const [priority, setPriority] = useState('medium');
  const priorityOptions = [
    { label: 'High', value: 'high' },
    { label: 'Medium', value: 'medium' },
    { label: 'Low', value: 'low' },
  ];

  // Add a state to track if "All Offices" is selected
  const [selectAllOffices, setSelectAllOffices] = useState(false);

  // Populate the form with taskToEdit data if in edit mode
  useEffect(() => {
    if (isEditMode && taskToEdit) {
      setTaskName(taskToEdit.name || '');
      setMaxTaskDuration(taskToEdit.max_task_duration || '');
      setDefaultTaskDuration(taskToEdit.default_task_duration || '');
      setPriority(taskToEdit.priority || 'medium');

      // Set public zone if available
      if (taskToEdit.public_zone && publicZoneOptions.length > 0) {
        const publicZone = publicZoneOptions.find(
          zone => zone.value === taskToEdit.public_zone,
        );
        if (publicZone) {
          setSelectedPublicZone(publicZone);
        }
      }
      if (taskToEdit.related_office) {
        const relatedOffices = taskToEdit.related_office.map(office => ({
          label: office.name,
          value: office.id,
        }));
        setSelectedOffices(relatedOffices);
      }

      // We'll handle office selection in the useEffect that watches officesShortList
    }
  }, [isEditMode, taskToEdit, publicZoneOptions]);

  useEffect(() => {
    if (publicZoneList && publicZoneList.length > 0) {
      let newArr = [];
      publicZoneList.forEach(zone => {
        newArr.push({
          label: zone.name,
          value: zone.id,
        });
      });
      setPublicZoneOptions(newArr);

      // Only set default if not in edit mode or no selected zone yet
      if (!isEditMode || !selectedPublicZone) {
        setSelectedPublicZone(newArr[0]);
      }
    }
  }, [publicZoneList]);

  useEffect(() => {
    if (selectedPublicZone?.value && getOfficesShortList) {
      getOfficesShortList({
        public_zone_id: selectedPublicZone.value,
      });
    } else if (getOfficesShortList) {
      getOfficesShortList();
    }
  }, [selectedPublicZone]);

  useEffect(() => {
    if (officesShortList && officesShortList.length > 0) {
      let newArr = [];
      officesShortList.forEach(office => {
        newArr.push({
          label: office.name,
          value: office.id,
        });
      });
      setOfficeOptions(newArr);

      // If in edit mode and we have related offices, set them
      if (isEditMode && taskToEdit && taskToEdit.related_office) {
        // Check if all offices are selected in edit mode
        if (taskToEdit.related_office.length === officesShortList.length) {
          setSelectAllOffices(true);
          setSelectedOffices(newArr); // Select all offices
        } else {
          setSelectAllOffices(false);
          const relatedOffices = taskToEdit.related_office.map(office => ({
            label: office.name,
            value: office.id,
          }));
          setSelectedOffices(relatedOffices);
        }
      }
    }
  }, [officesShortList, isEditMode, taskToEdit]);

  const onPublicZoneChange = e => {
    setSelectedPublicZone(e);
    // Reset offices when public zone changes
    setSelectedOffices([]);
    setSelectAllOffices(false);
  };

  const onOfficeChange = selectedOptions => {
    // Check if "All Offices" is among the selected options
    const allOfficesOption = selectedOptions.find(option => option.value === 'all');

    if (allOfficesOption) {
      // If "All Offices" is selected, select all office options
      setSelectAllOffices(true);
      setSelectedOffices(officeOptions);
    } else {
      // Regular selection
      setSelectAllOffices(false);
      setSelectedOffices(selectedOptions);
    }

    if (saveErrors?.office) {
      setSaveErrors({ ...saveErrors, office: undefined });
    }
  };

  const checkError = () => {
    let errors = {};
    let formIsValid = true;

    if (!taskName.trim()) {
      formIsValid = false;
      errors['taskName'] = 'Task name is required';
    } else if (checkUrl(taskName)) {
      formIsValid = false;
      errors['taskName'] = 'Task name cannot contain url';
    } else if (checkHtml(taskName)) {
      formIsValid = false;
      errors['taskName'] = 'Invalid task name';
    }

    if (!selectedPublicZone) {
      formIsValid = false;
      errors['public_zone'] = 'Public Zone is required';
    }

    if (!selectedOffices || selectedOffices.length === 0) {
      formIsValid = false;
      errors['office'] = 'At least one office must be selected';
    }

    // if (!maxTaskDuration) {
    //   formIsValid = false;
    //   errors['maxTaskDuration'] = 'Maximum task duration is required';
    // } else if (isNaN(maxTaskDuration) || maxTaskDuration <= 0) {
    //   formIsValid = false;
    //   errors['maxTaskDuration'] =
    //     'Maximum task duration must be a positive number';
    // }

    // if (!defaultTaskDuration) {
    //   formIsValid = false;
    //   errors['defaultTaskDuration'] = 'Default task duration is required';
    // } else if (isNaN(defaultTaskDuration) || defaultTaskDuration <= 0) {
    //   formIsValid = false;
    //   errors['defaultTaskDuration'] =
    //     'Default task duration must be a positive number';
    // } else if (Number(defaultTaskDuration) > Number(maxTaskDuration)) {
    //   formIsValid = false;
    //   errors['defaultTaskDuration'] =
    //     'Default duration cannot be greater than maximum duration';
    // }

    if (!priority) {
      formIsValid = false;
      errors['priority'] = 'Priority is required';
    }

    setSaveErrors(errors);
    return formIsValid;
  };

  const handleSubmit = () => {
    if (checkError()) {
      // Prepare the office IDs for submission
      let offices = [];
      if (selectAllOffices) {
        // If "All Offices" is selected, include all office IDs
        offices = officeOptions.map(office => office.value);
      } else {
        // Otherwise, use the selected offices
        offices = selectedOffices.map(office => office.value);
      }

      const payload = {
        name: taskName,
        max_task_duration: Number(maxTaskDuration),
        default_task_duration: Number(defaultTaskDuration),
        public_zone: selectedPublicZone.value,
        related_office_ids: offices,
        priority: priority,
      };


      if (isEditMode && taskToEdit) {
        editMasterTask({
          ...payload,
          task_id: taskToEdit.id,
        });
      } else {
        addMasterTask(payload);
      }
    }
  };

  const clearState = () => {
    setTaskName('');
    setMaxTaskDuration('');
    setDefaultTaskDuration('');
    setSaveErrors({});
    setSelectedPublicZone('');
    setSelectedOffices([]);
    setPriority('medium');
  };

  const handleToggle = () => {
    clearState();
    toggle();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleToggle}
      style={{ marginTop: `2vh`, maxWidth: `600px`, height: `auto`, overflow: `auto` }}
    >
      <ModalBody style={{ padding: `20px`, overflow: `auto` }}>
        <CommonText name title fontSize="16px" margin="auto 0 20px 0">
          {isEditMode ? t('edit_master_task') : t('add_master_task')}
        </CommonText>

        <InputWithLabelSection margin="20px 0 0">
          <InputLabel>
            {t('task_name')} <RedStar>*</RedStar>
          </InputLabel>
          <InputField
            type="text"
            placeholder={t('type_task_name')}
            value={taskName}
            onChange={e => {
              setTaskName(e.target.value);
              if (saveErrors?.taskName) {
                setSaveErrors({ ...saveErrors, taskName: undefined });
              }
            }}
            style={
              saveErrors?.taskName && {
                border: `1px solid #fe5969`,
                backgroundColor: `rgba(252, 87, 104, 0.05)`,
              }
            }
          />
          {saveErrors?.taskName && <FormAlert>{saveErrors.taskName}</FormAlert>}
        </InputWithLabelSection>

        <InputWithLabelSection margin="20px 0 0">
          <InputLabel>
            {t('public_zone')} <RedStar>*</RedStar>
          </InputLabel>
          <Select
            isSearchable
            value={selectedPublicZone}
            options={publicZoneOptions}
            placeholder={t('select_zone')}
            onChange={onPublicZoneChange}
            styles={CreateProjectDropdown()}
            filterOption={filterOption}
          />
          {saveErrors?.public_zone && (
            <FormAlert margin="0 0 0 5px">{saveErrors.public_zone}</FormAlert>
          )}
        </InputWithLabelSection>

        <InputWithLabelSection margin="20px 0 0">
          <InputLabel>
            {t('office')} <RedStar>*</RedStar>
          </InputLabel>
          <CustomCheckboxDropdown
            options={[
              // Add "All Offices" option at the top
              { label: t('all_offices'), value: 'all' },
              ...officeOptions
            ]}
            selectedOptions={selectAllOffices ? officeOptions : selectedOffices}
            onChange={onOfficeChange}
            placeholder={t('select_offices')}
            countLabel={t('offices_selected')}
            error={saveErrors && saveErrors['office']}
            errorMessage={saveErrors && saveErrors['office']}
            disabled={!selectedPublicZone}
            direction="up"
          />
        </InputWithLabelSection>
        {/* <InputWithLabelSection margin="20px 0 0">
          <InputLabel>
            {t('priority')} <RedStar>*</RedStar>
          </InputLabel>
          <Select
            value={priorityOptions.find(option => option.value === priority)}
            options={priorityOptions}
            onChange={selectedOption => {
              setPriority(selectedOption.value);
              if (saveErrors?.priority) {
                setSaveErrors({ ...saveErrors, priority: undefined });
              }
            }}
            styles={CreateProjectDropdown()}
          />
          {saveErrors?.priority && <FormAlert>{saveErrors.priority}</FormAlert>}
        </InputWithLabelSection> */}
        {/* <InputWithLabelSection margin="20px 0 0">
          <InputLabel>
            {t('max_task_duration')} ({t('hour')}) <RedStar>*</RedStar>
          </InputLabel>
          <InputField
            type="number"
            placeholder={t('enter_max_duration')}
            value={maxTaskDuration}
            onChange={e => {
              setMaxTaskDuration(e.target.value);
              if (saveErrors?.maxTaskDuration) {
                setSaveErrors({ ...saveErrors, maxTaskDuration: undefined });
              }
            }}
            style={
              saveErrors?.maxTaskDuration && {
                border: `1px solid #fe5969`,
                backgroundColor: `rgba(252, 87, 104, 0.05)`,
              }
            }
          />
          {saveErrors?.maxTaskDuration && (
            <FormAlert>{saveErrors.maxTaskDuration}</FormAlert>
          )}
        </InputWithLabelSection>

        <InputWithLabelSection margin="20px 0 0">
          <InputLabel>
            {t('default_task_duration')} ({t('hour')}) <RedStar>*</RedStar>
          </InputLabel>
          <InputField
            type="number"
            placeholder={t('enter_default_duration')}
            value={defaultTaskDuration}
            onChange={e => {
              setDefaultTaskDuration(e.target.value);
              if (saveErrors?.defaultTaskDuration) {
                setSaveErrors({
                  ...saveErrors,
                  defaultTaskDuration: undefined,
                });
              }
            }}
            style={
              saveErrors?.defaultTaskDuration && {
                border: `1px solid #fe5969`,
                backgroundColor: `rgba(252, 87, 104, 0.05)`,
              }
            }
          />
          {saveErrors?.defaultTaskDuration && (
            <FormAlert>{saveErrors.defaultTaskDuration}</FormAlert>
          )}
        </InputWithLabelSection> */}

        <CommonGrid
          justifyContent="unset"
          padding="10px 0 0 0"
          margin="30px 0 0"
        >
          <WhiteButton onClick={handleToggle}>{t('cancel')}</WhiteButton>
          <PrimaryButton onClick={handleSubmit}>
            {isLoading ? (
              <ButtonTextLoader
                loadingText={isEditMode ? t('updating') : t('adding')}
                fontSize="13px"
              />
            ) : isEditMode ? (
              t('update_task')
            ) : (
              t('add_task')
            )}
          </PrimaryButton>
        </CommonGrid>
      </ModalBody>
    </Modal>
  );
};

export default AddMasterTaskModal;
